import  { FC, useEffect, useMemo, useState } from 'react';
import { Tooltip } from '@mantine/core';

interface RelativeTimeProps {
    value?: string | Date | null;
}

const getRelativeTime = (target: Date, now: Date): string => {
    const diffMs = target.getTime() - now.getTime();
    const diffSeconds = Math.round(diffMs / 1000);

    const rtf = new Intl.RelativeTimeFormat(undefined, {
        numeric: 'auto',
    });

    const divisions: { amount: number; unit: Intl.RelativeTimeFormatUnit }[] = [
        { amount: 60, unit: 'second' },
        { amount: 60, unit: 'minute' },
        { amount: 24, unit: 'hour' },
        { amount: 7, unit: 'day' },
        { amount: 4.34524, unit: 'week' },
        { amount: 12, unit: 'month' },
        { amount: Number.POSITIVE_INFINITY, unit: 'year' },
    ];

    let duration = diffSeconds;
    for (const division of divisions) {
        if (Math.abs(duration) < division.amount) {
            return rtf.format(duration, division.unit);
        }
        duration = Math.round(duration / division.amount);
    }

    return rtf.format(duration, 'year');
};

export const RelativeTime: FC<RelativeTimeProps> = ({ value }) => {
    const [now, setNow] = useState<Date>(() => new Date());

    useEffect(() => {
        const interval = window.setInterval(() => {
            setNow(new Date());
        }, 30_000); // update every 30 seconds

        return () => {
            window.clearInterval(interval);
        };
    }, []);

    const parsedDate = useMemo(() => {
        if (!value) return null;
        const date = value instanceof Date ? value : new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    }, [value]);

    const { relativeText, fullText } = useMemo(() => {
        if (!parsedDate) {
            return {
                relativeText: '—',
                fullText: 'No date available',
            };
        }

        const relativeText = getRelativeTime(parsedDate, now);
        const fullText = parsedDate.toLocaleString(undefined, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });

        return { relativeText, fullText };
    }, [parsedDate, now]);

    return (
        <Tooltip label={fullText} withArrow>
            <span>{relativeText}</span>
        </Tooltip>
    );
};

export default RelativeTime;


